import React, { useState } from 'react'
import { useMessages, useSendMessage, useUnreadCount } from '../lib/hooks'
import { Button } from '../components/ui/Button'
import { Input, Textarea } from '../components/ui/Input'
import { Modal, FormModal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { LoadingSpinner, LoadingCard } from '../components/ui/LoadingSpinner'
import { 
  Plus, 
  Search, 
  Filter, 
  MessageCircle, 
  Send,
  Mail,
  MailOpen,
  Clock,
  User
} from 'lucide-react'
import { formatRelativeTime } from '../lib/utils'

export const CommunicationsPage: React.FC = () => {
  const [messageType, setMessageType] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [showComposeModal, setShowComposeModal] = useState(false)

  // API hooks
  const { data: messages, isLoading } = useMessages({
    message_type: messageType,
    unread_only: unreadOnly,
    limit: 50,
  })

  const { data: unreadCount } = useUnreadCount()
  const sendMessageMutation = useSendMessage()

  const handleSendMessage = async (data: any) => {
    try {
      await sendMessageMutation.mutateAsync(data)
      setShowComposeModal(false)
    } catch (error) {
      // Error handled by mutation
    }
  }

  const messageTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'notification', label: 'Notifications' },
    { value: 'alert', label: 'Alerts' },
    { value: 'system', label: 'System Messages' },
    { value: 'user', label: 'User Messages' },
  ]

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-orange-100 text-orange-800'
      case 'normal':
        return 'bg-blue-100 text-blue-800'
      case 'low':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
          <p className="text-gray-600">
            Messages and notifications
            {unreadCount?.unread_count > 0 && (
              <span className="ml-2 bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                {unreadCount.unread_count} unread
              </span>
            )}
          </p>
        </div>
        
        <Button onClick={() => setShowComposeModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Compose Message
        </Button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                placeholder="Search messages..."
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>
            
            <select
              value={messageType}
              onChange={(e) => setMessageType(e.target.value)}
              className="input"
            >
              {messageTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm">Unread only</span>
            </label>
            
            <Button
              variant="outline"
              onClick={() => {
                setMessageType('')
                setUnreadOnly(false)
              }}
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <LoadingCard key={i} />
          ))
        ) : messages?.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No messages found</h3>
            <p className="text-gray-500">
              {unreadOnly ? 'No unread messages' : 'No messages to display'}
            </p>
          </div>
        ) : (
          messages?.map((message) => (
            <MessageCard key={message.id} message={message} />
          ))
        )}
      </div>

      {/* Compose Message Modal */}
      <ComposeMessageModal
        isOpen={showComposeModal}
        onClose={() => setShowComposeModal(false)}
        onSend={handleSendMessage}
        loading={sendMessageMutation.isLoading}
      />
    </div>
  )
}

// Message Card Component
interface MessageCardProps {
  message: any
}

const MessageCard: React.FC<MessageCardProps> = ({ message }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-orange-100 text-orange-800'
      case 'normal':
        return 'bg-blue-100 text-blue-800'
      case 'low':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className={`card ${!message.is_read ? 'border-blue-200 bg-blue-50' : ''}`}>
      <div className="card-content">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              {message.is_read ? (
                <MailOpen className="w-5 h-5 text-gray-400" />
              ) : (
                <Mail className="w-5 h-5 text-blue-600" />
              )}
              
              <h3 className="text-lg font-semibold text-gray-900">
                {message.subject}
              </h3>
              
              <Badge className={getPriorityColor(message.priority)}>
                {message.priority}
              </Badge>
              
              <Badge variant="outline">
                {message.message_type}
              </Badge>
            </div>
            
            <div className="flex items-center space-x-4 mb-3 text-sm text-gray-600">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                From: {message.sender.full_name}
              </div>
              
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {formatRelativeTime(message.created_at)}
              </div>
              
              {message.recipients && message.recipients.length > 0 && (
                <div>
                  To: {message.recipients.map(r => r.full_name).join(', ')}
                </div>
              )}
            </div>
            
            <div className="text-gray-700 mb-3">
              {message.body}
            </div>
            
            {message.related_project && (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>Related Project:</strong> {message.related_project.title}
                </p>
              </div>
            )}
            
            {message.related_proposal && (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>Related Proposal:</strong> {message.related_proposal.vendor_name}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {!message.is_read && (
              <Button variant="outline" size="sm">
                Mark as Read
              </Button>
            )}
            
            <Button variant="outline" size="sm">
              Reply
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Compose Message Modal Component
interface ComposeMessageModalProps {
  isOpen: boolean
  onClose: () => void
  onSend: (data: any) => void
  loading: boolean
}

const ComposeMessageModal: React.FC<ComposeMessageModalProps> = ({
  isOpen,
  onClose,
  onSend,
  loading,
}) => {
  const [formData, setFormData] = useState({
    recipient_usernames: [''],
    subject: '',
    body: '',
    message_type: 'user',
    priority: 'normal',
    send_email: false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSend(formData)
  }

  const addRecipient = () => {
    setFormData(prev => ({
      ...prev,
      recipient_usernames: [...prev.recipient_usernames, '']
    }))
  }

  const removeRecipient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      recipient_usernames: prev.recipient_usernames.filter((_, i) => i !== index)
    }))
  }

  const updateRecipient = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      recipient_usernames: prev.recipient_usernames.map((username, i) => 
        i === index ? value : username
      )
    }))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Compose Message"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipients
          </label>
          {formData.recipient_usernames.map((username, index) => (
            <div key={index} className="flex items-center space-x-2 mb-2">
              <Input
                placeholder="Enter username..."
                value={username}
                onChange={(e) => updateRecipient(index, e.target.value)}
                className="flex-1"
              />
              {index > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeRecipient(index)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRecipient}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Recipient
          </Button>
        </div>
        
        <Input
          label="Subject"
          value={formData.subject}
          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
          required
        />
        
        <Textarea
          label="Message"
          value={formData.body}
          onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
          rows={6}
          required
        />
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Type
            </label>
            <select
              value={formData.message_type}
              onChange={(e) => setFormData(prev => ({ ...prev, message_type: e.target.value }))}
              className="input"
            >
              <option value="user">User Message</option>
              <option value="notification">Notification</option>
              <option value="alert">Alert</option>
              <option value="system">System Message</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="input"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.send_email}
            onChange={(e) => setFormData(prev => ({ ...prev, send_email: e.target.checked }))}
            className="rounded border-gray-300"
          />
          <span className="text-sm">Send email notification</span>
        </label>
        
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            loading={loading}
            disabled={!formData.subject || !formData.body || formData.recipient_usernames.filter(Boolean).length === 0}
          >
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default CommunicationsPage